import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export default class HTTPServerResponseNode extends BaseNode {
    constructor() {
        super();
        this.name = 'HTTP Response';
        this.id = 'node_http_server_response';
        this.linkedFeature = 'httpserver';
        this.requirements = ['network'];

        this.inputs = [
            {
                name: 'Response',
                portId: 'http_response',
                params: [
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'STATUS_CODE',
                name: 'Status Code',
                settingType: SettingType.OPTION,
                required: true,
                default: '200',
                options: [
                    {
                        name: '200',
                        value: '200',
                    },
                    {
                        name: '404',
                        value: '404',
                    },
                    {
                        name: '500',
                        value: '500',
                    },
                    {
                        name: '401',
                        value: '401',
                    },
                    {
                        name: '403',
                        value: '403',
                    },
                ],
            },
            {
                id: 'RESPONSE_DATA',
                name: 'Response Data',
                settingType: SettingType.STRINGVARIABLE,
                dataType: TibboDataType.STRING,
                required: true,
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    validate(): ValidationMessage[] {
        const messages: ValidationMessage[] = [];
        const links = this.project.getNodeLinks(this, 'http_response');
        if (links.length !== 1) {
            messages.push({
                messageType: ValidationMessageType.ERROR,
                message: 'On HTTP Request block is required and can only be linked to one block',
                featureId: this.getID(),
            });
        }
        // get block of previous node
        const block = this.project.getFeature(links[0].getSettingValue('SOURCE_FEATURE'));
        if (!block || block.id !== 'node_http_server_endpoint') {
            messages.push({
                messageType: ValidationMessageType.ERROR,
                message: 'On HTTP Request block is required and can only be linked to one On HTTP Request block',
                featureId: this.getID(),
            });
        }
        return messages;
    }

    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project = this.project;
        const STATUS_CODE = this.getSettingValue('STATUS_CODE');
        const RESPONSE_DATA = this.getSettingValue('RESPONSE_DATA');
        const links = project.getNodeLinks(this, 'http_response');
        if (links.length !== 1) {
            return blocks;
        }
        // get block of previous node
        const block = this.project.getFeature(links[0].getSettingValue('SOURCE_FEATURE'));
        if (!block || block.id !== 'node_http_server_endpoint') {
            return blocks;
        }
        const endpoint = project.getFeature(block.getSettingValue('ENDPOINT'));
        if (!endpoint) {
            return blocks;
        }
        const PATH = endpoint.getSettingValue('PATH');

        blocks.push({
            location: `HTTP_ENDPOINT_REQUESTED_HANDLE_CUSTOM_${PATH}`,
            blockType: BlockType.CODE,
            template: `handle_response = true`,
        });

        const link = links[0];
        const source: BaseNode = project.getFeature(link.getSettingValue('SOURCE_FEATURE')) as BaseNode;
        const sourceParams = source.getPort(link.getSettingValue('SOURCE_PORT'))?.params;

        const doneLinks = project.getNodeLinks(this, 'done');
        const doneBlock = doneLinks.length > 0 ? `${project.getCodeLocationStart()}${doneLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : '';
        blocks.push({
            location: `HTTP_ENDPOINT_REQUESTED_${PATH}`,
            blockType: BlockType.CODE,
            template: `
http_server_response_status(${STATUS_CODE})
http_server_response_header_end()
sock.setdata(${project.parseCodeExpression(RESPONSE_DATA, sourceParams, TibboDataType.STRING)})
sock.send()
${doneBlock}
`,
        });
        return blocks;
    }

    getDetails(): string {
        return `##### Response to HTTP ENDPOINT`;
    }
}
